import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Label } from '@/components/ui/label';
import type { FormInput } from './InputInterface';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/hooks/useTranslation';

type MultipleCheckboxProps<T extends string> = FormInput<T[]> & {
  options: { label: string; value: T }[];
  requiredMulti?: boolean;
};

export const MultipleCheckbox = forwardRef(function MultipleCheckbox<T extends string>(
  {
    name,
    label,
    value,
    onChange,
    options,
    error,
    required,
    requiredMulti = false,
  }: MultipleCheckboxProps<T>,
  ref: React.Ref<{ validate: () => boolean }>
) {
  const groupRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleChange = (checkedValue: T, isChecked: boolean) => {
    const newValue = isChecked
      ? [...(value ?? []), checkedValue]
      : (value ?? []).filter((v) => v !== checkedValue);

    onChange(newValue);
  };

  // expose validate() au parent
  useImperativeHandle(ref, () => ({
    validate: () => {
      if (required && (!value || value.length === 0)) {
        // Utilise setCustomValidity sur le premier input pour affichage natif
        const firstInput = groupRef.current?.querySelector('input');
        if (firstInput) {
          firstInput.setCustomValidity(t('form.validation.checkboxAtLeastOne'));
          firstInput.reportValidity();
          firstInput.setCustomValidity('');
          return false;
        }
      }
      return true;
    },
  }));

  return (
    <div className="space-y-4 w-full" ref={groupRef}>
      <Label htmlFor={name} className="flex items-center space-x-1">
        <span>{label}</span>
        {required && <span className="text-destructive">*</span>}
        {requiredMulti && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help text-warning">*</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{t('form.validation.requiredMultiTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>

      <div className="flex flex-col space-y-2 w-full">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              name={name}
              value={opt.value}
              checked={(value ?? []).includes(opt.value)}
              onChange={(e) => handleChange(opt.value, e.target.checked)}
              className="accent-blue-600"
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
});
